import { useMemo } from "react";
import { doNotImport } from "../lib/spreadsheet-upload-config";
import {
  camelCaseWordWithUnderscore,
  cn,
  convertArrayToSentence,
} from "../lib/utils";
import SearchableSelectInput from "./searchable-select-input";
import { SpreadSheetState } from "./spread-sheet-upload-flow";
import { Check, X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from "./ui/table";

// Define interfaces for the data structures
interface SupportedColumn {
  value: string;
  label: string;
  icon?: string;
  type: string;
  isRequired: boolean;
}

interface MappedColumn {
  originalColumn: string;
  supportedColumn: string;
  supportedColumnDataType: string;
  supportedColumnLabel: string;
  isRequired: boolean;
}

interface SelectOption {
  disabled: boolean;
  icon?: string;
  label: string;
  value: string;
}

interface SpreadSheetColumnMappingProps {
  uploadedSpreadSheet: SpreadSheetState | null;
  setUploadedSpreadSheet: (state: SpreadSheetState) => void;
  missingRequiredColumns: boolean;
}

const SpreadSheetColumnMapping: React.FC<SpreadSheetColumnMappingProps> = ({
  uploadedSpreadSheet,
  setUploadedSpreadSheet,
  missingRequiredColumns,
}) => {
  const selectOptions = useMemo<SelectOption[]>(() => {
    return (
      uploadedSpreadSheet?.supportedColumns.map((column) => {
        let mappedTo = "";
        const originalColumnIndex =
          uploadedSpreadSheet?.mappedColumns.findIndex(
            (item) => item.supportedColumn === column.value
          );
        if (originalColumnIndex !== -1) {
          mappedTo =
            uploadedSpreadSheet?.mappedColumns[originalColumnIndex]
              .originalColumn;
        }
        return {
          disabled: column.value === doNotImport ? false : mappedTo !== "",
          icon: column.icon,
          label:
            column.value === doNotImport
              ? column.label
              : column.label +
                (mappedTo !== ""
                  ? ` mapped to (${camelCaseWordWithUnderscore(mappedTo)})`
                  : ""),
          value: column.value,
        };
      }) ?? []
    );
  }, [uploadedSpreadSheet]);

  return (
    <div className="flex flex-grow flex-col space-y-8 px-6">
      <div className="flex flex-col space-y-4 text-center">
        <h5>Match Columns</h5>
        <p>Select the correct field for each column you'd like to import</p>
        {missingRequiredColumns && (
          <p className="text-red-600">
            Your data feed file should contain mandatory columns which are{" "}
            {convertArrayToSentence({
              array:
                uploadedSpreadSheet?.supportedColumns.filter(
                  (item) => item.isRequired
                ) ?? [],
              property: "label",
            })}
          </p>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableHeaderRow>
            <TableColumnHeader>IMPORTED DATA</TableColumnHeader>
            <TableColumnHeader>SAFARISYNC FIELDS</TableColumnHeader>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {uploadedSpreadSheet?.mappedColumns.map((column, index) => (
            <TableRow key={index}>
              <TableCell className="w-1/2 p-3 text-center">
                <div className="relative flex items-center space-x-2">
                  <div>
                    {camelCaseWordWithUnderscore(column.originalColumn)}
                  </div>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 transform">
                    {column.supportedColumn !== "" &&
                      column.supportedColumn !== doNotImport && (
                        <Check className="text-green-600 w-4" />
                      )}
                    {(column.supportedColumn === "" ||
                      column.supportedColumn === doNotImport) && (
                      <X className="text-red-600 w-4" />
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="w-1/2 p-3 text-center">
                <SearchableSelectInput
                  options={selectOptions}
                  value={column.supportedColumn}
                  onSelect={(value: string) => {
                    const supportedColumn =
                      uploadedSpreadSheet?.supportedColumns.find(
                        (item) => item.value === value
                      );
                    if (!supportedColumn || !uploadedSpreadSheet) return;

                    const newColumns = [...uploadedSpreadSheet.mappedColumns];
                    newColumns[index] = {
                      ...newColumns[index],
                      supportedColumn: value,
                      supportedColumnDataType: supportedColumn.type ?? "",
                      supportedColumnLabel: supportedColumn.label,
                      isRequired: supportedColumn.isRequired ?? false,
                    };

                    const newFileUploadState: SpreadSheetState = {
                      ...uploadedSpreadSheet,
                      mappedColumns: newColumns,
                    };
                    setUploadedSpreadSheet(newFileUploadState);
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default SpreadSheetColumnMapping;
